import jsPDF from 'jspdf';

export const generateInvoicePDF = (invoice, customer) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape mode
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Safety check for items
  const items = invoice.items || [];

  // === HEADER ===
  doc.setFillColor(180, 120, 50); // Brown color
  doc.rect(0, 0, pageWidth, 15, 'F');
  
  doc.setFillColor(107, 27, 154);
  doc.circle(8, 7.5, 3, 'F');
  
  doc.setDrawColor(0, 188, 212);
  doc.setLineWidth(0.8);
  doc.line(6, 6, 2, 3);
  
  doc.setDrawColor(0, 230, 118);
  doc.setLineWidth(0.8);
  doc.line(6, 7.5, 1, 6);
  
  doc.setDrawColor(255, 111, 0);
  doc.setLineWidth(0.8);
  doc.line(6, 9, 2, 10);
  
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(18);
  doc.text('SSP BILLING SYSTEM', 20, 11);

  // === DOCUMENT TITLE & DETAILS ===
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('TAX INVOICE', 15, 22);

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Invoice No: ${invoice.invoiceNumber}`, 15, 28);
  doc.text(`Date: ${new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}`, 15, 33);
  doc.text(`Payment Mode: ${invoice.paymentMode || 'Not Specified'}`, 15, 38);

  // === CUSTOMER DETAILS BOX ===
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.rect(5, 42, 280, 22);
  
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.text('BILL TO', 10, 46);
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  doc.text(`Name: ${customer.fullName || 'N/A'}`, 10, 51);
  doc.text(`Phone: ${customer.phoneNumber || 'N/A'}`, 10, 56);
  doc.text(`GSTIN: ${customer.gstinNumber || 'N/A'}`, 10, 61);

  // === PRODUCT DETAILS TABLE ===
  let tableY = 70;
  
  doc.setFillColor(15, 52, 96);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  
  const columns = ['SL', 'Product Name', 'HSN Code', 'Qty', 'Rate', 'Tax %', 'Subtotal', 'Tax Amt', 'Total'];
  const columnWidths = [8, 50, 18, 12, 18, 12, 20, 20, 22];
  let colX = 5;
  
  columns.forEach((col, idx) => {
    doc.rect(colX, tableY - 5, columnWidths[idx], 5, 'F');
    doc.text(col, colX + 1, tableY - 1);
    colX += columnWidths[idx];
  });

  tableY += 3;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  // Add product rows
  let totalCGST = 0;
  let totalSGST = 0;
  let totalIGST = 0;

  items.forEach((item, idx) => {
    const taxAmount = item.cgst + item.sgst + item.igst;
    const subtotal = item.rate * item.quantity;
    const total = subtotal + taxAmount;
    
    totalCGST += item.cgst;
    totalSGST += item.sgst;
    totalIGST += item.igst;
    
    colX = 5;
    const values = [
      idx + 1,
      item.productName,
      item.hsnCode || 'N/A',
      item.quantity,
      `₹${item.rate.toFixed(2)}`,
      `${item.taxRate || 0}%`,
      `₹${subtotal.toFixed(2)}`,
      `₹${taxAmount.toFixed(2)}`,
      `₹${total.toFixed(2)}`
    ];
    
    values.forEach((val, idx) => {
      doc.text(val.toString(), colX + 1, tableY);
      colX += columnWidths[idx];
    });
    
    tableY += 5;
  });

  // === TAX BREAKDOWN TABLE ===
  tableY += 5;
  doc.setFillColor(15, 52, 96);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.text('TAX BREAKDOWN', 15, tableY);

  tableY += 8;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.setFillColor(230, 240, 250);
  
  const taxLabels = ['CGST', 'SGST', 'IGST', 'TOTAL TAX'];
  const totalTax = totalCGST + totalSGST + totalIGST;
  
  const taxValues = [
    `₹${totalCGST.toFixed(2)}`,
    `₹${totalSGST.toFixed(2)}`,
    `₹${totalIGST.toFixed(2)}`,
    `₹${totalTax.toFixed(2)}`
  ];

  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  taxLabels.forEach((label, idx) => {
    doc.rect(15, tableY - 2, 60, 6, 'F');
    doc.setFont(undefined, 'bold');
    doc.text(label, 20, tableY + 1);
    doc.setFont(undefined, 'normal');
    doc.text(taxValues[idx], 70, tableY + 1, { align: 'right' });
    tableY += 7;
  });

  // === TOTALS SUMMARY ===
  tableY += 5;
  const subtotal = items.reduce((sum, item) => sum + (item.rate * item.quantity), 0);
  const grandTotal = subtotal + totalTax;

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  
  doc.text('Subtotal:', 15, tableY);
  doc.text(`₹ ${subtotal.toFixed(2)}`, 85, tableY, { align: 'right' });
  
  tableY += 6;
  doc.text('Total Tax:', 15, tableY);
  doc.text(`₹ ${totalTax.toFixed(2)}`, 85, tableY, { align: 'right' });

  // Grand Total Box
  tableY += 8;
  doc.setFillColor(15, 52, 96);
  doc.rect(15, tableY - 4, 70, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('GRAND TOTAL', 17, tableY + 2);
  doc.text(`₹ ${grandTotal.toFixed(2)}`, 82, tableY + 2, { align: 'right' });

  // === FOOTER ===
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('This is a computer generated invoice. Signature not required.', pageWidth / 2, pageHeight - 5, { align: 'center' });

  return doc;
};

export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};

export const downloadJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
};

export const generatePurchasePDF = (purchase) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape mode
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Safety check for items
  const items = purchase.items || [];

  // === HEADER ===
  doc.setFillColor(180, 120, 50); // Brown color
  doc.rect(0, 0, pageWidth, 15, 'F');
  
  doc.setFillColor(107, 27, 154);
  doc.circle(8, 7.5, 3, 'F');
  
  doc.setDrawColor(0, 188, 212);
  doc.setLineWidth(0.8);
  doc.line(6, 6, 2, 3);
  
  doc.setDrawColor(0, 230, 118);
  doc.setLineWidth(0.8);
  doc.line(6, 7.5, 1, 6);
  
  doc.setDrawColor(255, 111, 0);
  doc.setLineWidth(0.8);
  doc.line(6, 9, 2, 10);
  
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(18);
  doc.text('SSP BILLING SYSTEM', 20, 11);

  // === DOCUMENT TITLE & DETAILS ===
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('PURCHASE RECORD', 15, 22);

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(`Purchase No: ${purchase.purchaseNumber}`, 15, 28);
  doc.text(`Date: ${new Date(purchase.purchaseDate).toLocaleDateString('en-IN')}`, 15, 33);
  doc.text(`Payment Mode: ${purchase.paymentMode || 'Not Specified'}`, 15, 38);

  // === SUPPLIER DETAILS BOX ===
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.rect(5, 42, 280, 22);
  
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.text('SUPPLIER DETAILS', 10, 46);
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  doc.text(`Name: ${purchase.supplierName || 'N/A'}`, 10, 51);
  doc.text(`Phone: ${purchase.supplierPhone || 'N/A'}`, 10, 56);
  doc.text(`GSTIN: ${purchase.supplierGSTIN || 'N/A'}`, 10, 61);

  // === PRODUCT DETAILS TABLE ===
  let tableY = 70;
  
  doc.setFillColor(15, 52, 96);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(8);
  
  const columns = ['SL', 'Product Name', 'HSN Code', 'Qty', 'Rate', 'Tax %', 'Subtotal', 'Tax Amt', 'Total'];
  const columnWidths = [8, 50, 18, 12, 18, 12, 20, 20, 22];
  let colX = 5;
  
  columns.forEach((col, idx) => {
    doc.rect(colX, tableY - 5, columnWidths[idx], 5, 'F');
    doc.text(col, colX + 1, tableY - 1);
    colX += columnWidths[idx];
  });

  tableY += 3;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  // Add product rows
  items.forEach((item, idx) => {
    const taxAmount = item.cgst + item.sgst + item.igst;
    const subtotal = item.purchasePrice * item.quantity;
    const total = subtotal + taxAmount;
    
    colX = 5;
    const values = [
      idx + 1,
      item.productName,
      item.hsnCode || 'N/A',
      item.quantity,
      `₹${item.purchasePrice.toFixed(2)}`,
      `${item.taxRate || 0}%`,
      `₹${subtotal.toFixed(2)}`,
      `₹${taxAmount.toFixed(2)}`,
      `₹${total.toFixed(2)}`
    ];
    
    values.forEach((val, idx) => {
      doc.text(val.toString(), colX + 1, tableY);
      colX += columnWidths[idx];
    });
    
    tableY += 5;
  });

  // === TAX BREAKDOWN TABLE ===
  tableY += 5;
  doc.setFillColor(15, 52, 96);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.text('TAX BREAKDOWN', 15, tableY);

  tableY += 8;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.setFillColor(230, 240, 250);
  
  const taxLabels = ['CGST', 'SGST', 'IGST', 'TOTAL TAX'];
  const cgst = items.reduce((sum, item) => sum + item.cgst, 0);
  const sgst = items.reduce((sum, item) => sum + item.sgst, 0);
  const igst = items.reduce((sum, item) => sum + item.igst, 0);
  const totalTax = cgst + sgst + igst;
  
  const taxValues = [
    `₹${cgst.toFixed(2)}`,
    `₹${sgst.toFixed(2)}`,
    `₹${igst.toFixed(2)}`,
    `₹${totalTax.toFixed(2)}`
  ];

  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);

  taxLabels.forEach((label, idx) => {
    doc.rect(15, tableY - 2, 60, 6, 'F');
    doc.setFont(undefined, 'bold');
    doc.text(label, 20, tableY + 1);
    doc.setFont(undefined, 'normal');
    doc.text(taxValues[idx], 70, tableY + 1, { align: 'right' });
    tableY += 7;
  });

  // === TOTALS SUMMARY ===
  tableY += 5;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  
  doc.text('Subtotal:', 15, tableY);
  doc.text(`₹ ${purchase.subtotal.toFixed(2)}`, 85, tableY, { align: 'right' });
  
  tableY += 6;
  doc.text('Total Tax:', 15, tableY);
  doc.text(`₹ ${purchase.totalTax.toFixed(2)}`, 85, tableY, { align: 'right' });

  // Grand Total Box
  tableY += 8;
  doc.setFillColor(15, 52, 96);
  doc.rect(15, tableY - 4, 70, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('GRAND TOTAL', 17, tableY + 2);
  doc.text(`₹ ${purchase.grandTotal.toFixed(2)}`, 82, tableY + 2, { align: 'right' });

  // === FOOTER ===
  doc.setTextColor(128, 128, 128);
  doc.setFontSize(8);
  doc.text('This is a computer generated purchase record. Signature not required.', pageWidth / 2, pageHeight - 5, { align: 'center' });

  return doc;
};
